import { z } from "zod";
import { DOSE_INTERVAL_COPY, isOnceAWeek } from "./dose-interval";

// TES-1292 (TES-1267 §7–8; A-02/09C §7 "LÄKEMEDELSKATALOG I ADMIN") — the
// versioned medication catalogue the doctor picks the order from:
//   "Kirill ska inte behöva skriva läkemedelsnamn, styrka, dosformat eller intervall som ostrukturerad text."
//   "Ett avvikande fritextval kräver uttrycklig behörighet och motivering."
//
// THE ROLLOUT RULE (coordinator, 2026-10-13): the picker becomes the required
// path for a product only once a doctor has clinically approved its current
// version AND that version lists strengths and intervals. Before that the
// order fields stay free text for the product, as they were, with no
// motivation asked. `catalogueVersionInForce` is that rule, in one place,
// for admin and the server alike.

// The parts of an order that can carry a catalogue reference. Optional on
// the wire: an order without them is a free-text order, as every order was
// before the catalogue.
export const orderCatalogueFields = {
  // The MedicationProductVersion the parts were picked from.
  medicationVersionId: z.string().uuid().optional(),
  // The doctor's motivation for a deviation ("Annat (avvikande)"): a value
  // outside an approved entry, or a medication outside the catalogue.
  deviationReason: z.string().trim().max(1000).optional(),
};

export const medicationCatalogueVersionSchema = z.object({
  id: z.string(),
  version: z.number().int().min(1),
  strengths: z.array(z.string()),
  intervals: z.array(z.string()),
  instructionUrl: z.string().nullable(),
  instructionUrlActive: z.boolean(),
  filmUrl: z.string().nullable(),
  sourceSender: z.string().nullable(),
  language: z.string(),
  informationOwner: z.string().nullable(),
  contentSource: z.string().nullable(),
  changeNote: z.string().nullable(),
  createdAt: z.string(),
  createdByEmployeeId: z.string().nullable(),
  lastCheckedAt: z.string().nullable(),
  lastCheckedByEmployeeId: z.string().nullable(),
  clinicallyApprovedAt: z.string().nullable(),
  clinicallyApprovedByEmployeeId: z.string().nullable(),
  supersededAt: z.string().nullable(),
});
export type MedicationCatalogueVersion = z.infer<
  typeof medicationCatalogueVersionSchema
>;

export const medicationCatalogueProductSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  activeSubstance: z.string(),
  penVariant: z.string(),
  active: z.boolean(),
  current: medicationCatalogueVersionSchema,
  // Older versions, newest first. Only the catalogue screen asks for them.
  history: z.array(medicationCatalogueVersionSchema).optional(),
});
export type MedicationCatalogueProduct = z.infer<
  typeof medicationCatalogueProductSchema
>;

// GET employee-api/v1/medication-catalogue[?history=1]
export const medicationCatalogueSchema = z.object({
  products: z.array(medicationCatalogueProductSchema),
});
export type MedicationCatalogue = z.infer<typeof medicationCatalogueSchema>;

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((v) => (v ? v : null));

const httpsUrl = z
  .string()
  .trim()
  .max(500)
  .url()
  .refine((v) => v.startsWith("https://"), "Länken måste börja med https://");

// POST employee-api/v1/medication-catalogue/:productId/versions (admin) —
// the next version of an entry. Every field is sent whole: the new version
// replaces the current one, it does not patch it. A new version is never
// clinically approved, whatever the one before it was.
export const newMedicationCatalogueVersionSchema = z
  .object({
    strengths: z.array(z.string().trim().min(1).max(64)).max(30),
    // TES-1375 (Ö-419) — Mounjaro, Wegovy and Ozempic are all dosed once a
    // week, and Ella 2.1 presents no other interval: an entry may list only
    // the weekly value. An order picked from it then passes the order's own
    // weekly check.
    intervals: z
      .array(
        z
          .string()
          .trim()
          .min(1)
          .max(64)
          .refine((v) => isOnceAWeek(v), DOSE_INTERVAL_COPY.weeklyOnly),
      )
      .max(10),
    instructionUrl: httpsUrl.nullable(),
    instructionUrlActive: z.boolean(),
    filmUrl: httpsUrl.nullable(),
    sourceSender: optionalText(200),
    informationOwner: optionalText(200),
    contentSource: optionalText(1000),
    changeNote: z.string().trim().min(1).max(500),
  })
  .refine((v) => !v.instructionUrlActive || v.instructionUrl !== null, {
    message: "En aktiv länk kräver en adress.",
    path: ["instructionUrl"],
  });
export type NewMedicationCatalogueVersion = z.infer<
  typeof newMedicationCatalogueVersionSchema
>;

// The words the picker and the catalogue screen use.
export const MEDICATION_CATALOGUE_COPY = {
  notApproved: "ej kliniskt godkänd",
  deviation: "Annat (avvikande)",
  otherMedication: "Annat läkemedel (avvikande)",
  freeTextMedication: "Annat läkemedel (fritext)",
  deviationReasonLabel: "Motivering till avvikelsen",
  deviationReasonRequired: "Ange en motivering till avvikelsen.",
  // TES-1400 (Ö-541) — ours, from Emelie's "kräver särskild behörighet".
  deviationNotPermitted:
    "Annat (avvikande) kräver särskild behörighet. Välj ur katalogen eller be en administratör om behörigheten.",
} as const;

/** The product as the doctor picks it: "Mounjaro (KwikPen)". */
export function catalogueProductLabel(p: {
  name: string;
  penVariant: string;
}): string {
  return `${p.name} (${p.penVariant})`;
}

/**
 * The rollout rule: is the picker the required path for this version? Only
 * when a doctor has approved it and it has something to pick.
 */
export function catalogueVersionInForce(v: {
  clinicallyApprovedAt: string | Date | null;
  strengths: readonly string[];
  intervals: readonly string[];
}): boolean {
  return (
    v.clinicallyApprovedAt !== null &&
    v.strengths.length > 0 &&
    v.intervals.length > 0
  );
}

// The parts a deviation is judged on: the order's medication, dose and
// interval. The planned medication's separate "styrka" is not one of them:
// it is offered from the same list in admin, but it is not part of the
// prescription the patient reads, and an older plan's strength must not
// block an order that is otherwise picked from the catalogue.
export type CatalogueOrderParts = {
  medication?: string | null;
  dose?: string | null;
  frequency?: string | null;
};

/**
 * The order's parts that fall outside an in-force entry, in field order. A
 * blank part is not outside (a meeting draft may be half-written). Empty when
 * the version is not in force: before approval nothing is a deviation.
 */
export function catalogueDeviations(
  entry: {
    name: string;
    clinicallyApprovedAt: string | Date | null;
    strengths: readonly string[];
    intervals: readonly string[];
  },
  order: CatalogueOrderParts,
): Array<keyof CatalogueOrderParts> {
  if (!catalogueVersionInForce(entry)) return [];
  const out: Array<keyof CatalogueOrderParts> = [];
  const filled = (v: string | null | undefined) => (v ?? "").trim();
  const med = filled(order.medication);
  if (med && med !== entry.name) out.push("medication");
  const dose = filled(order.dose);
  if (dose && !entry.strengths.includes(dose)) out.push("dose");
  const freq = filled(order.frequency);
  if (freq && !entry.intervals.includes(freq)) out.push("frequency");
  return out;
}

// TES-1293 (TES-1267 §8; A-02/09C §8) — the weekly check of the official
// instruction links:
//   "Förändring skapar intern granskningsuppgift; den får aldrig automatiskt ändra medicinsk patientcopy."
// What a check can find. Any finding opens (or adds to) the version's one
// open internal review task; nothing else happens.
export const MEDICATION_SOURCE_FINDINGS = [
  // No 2xx answer: a timeout, a network error, a 4xx/5xx, too many redirects.
  "unreachable",
  // A redirect pointed off the link's own site. It is not followed.
  "redirected_off_domain",
  // The page no longer names the product, or its pen variant.
  "product_missing",
  "pen_variant_missing",
  // The page's text (as a hash) differs from the last run's.
  "content_changed",
  // The page's "Uppdaterad" date or its Last-Modified header moved.
  "update_date_changed",
  // TES-1361 — a guide source answered 404/410: the page is gone from the
  // address. Any other failed read of a guide source is `unreachable`
  // (TES-1494, Ö-596).
  "source_gone",
] as const;
export type MedicationSourceFinding =
  (typeof MEDICATION_SOURCE_FINDINGS)[number];

// The admin's words for a finding. Proposed copy (TES-1293).
export const MEDICATION_SOURCE_FINDING_LABEL: Record<
  MedicationSourceFinding,
  string
> = {
  unreachable: "Länken svarar inte",
  redirected_off_domain: "Länken skickar vidare till en annan webbplats",
  product_missing: "Preparatnamnet saknas på sidan",
  pen_variant_missing: "Pennvarianten saknas på sidan",
  content_changed: "Sidans innehåll har ändrats",
  update_date_changed: "Sidans uppdateringsdatum har ändrats",
  source_gone: "Källan finns inte längre på adressen",
};

// TES-1361 — the kinds of official source the injection guide register
// lists (CP-12 2026-10-14: FASS, EMA, Medicininstruktioner).
export const INJECTION_GUIDE_SOURCE_KINDS = [
  "fass",
  "ema",
  "medicininstruktioner",
] as const;
export type InjectionGuideSourceKind =
  (typeof INJECTION_GUIDE_SOURCE_KINDS)[number];

export const INJECTION_GUIDE_SOURCE_KIND_LABEL: Record<
  InjectionGuideSourceKind,
  string
> = {
  fass: "FASS",
  ema: "EMA",
  medicininstruktioner: "Medicininstruktioner",
};

export const medicationSourceCheckSchema = z.object({
  // A catalogue version's link ("instruction" | "film"), or a guide
  // register source (its kind).
  kind: z.enum(["instruction", "film", ...INJECTION_GUIDE_SOURCE_KINDS]),
  url: z.string(),
  checkedAt: z.string(),
  trigger: z.enum(["cron", "manual"]),
  httpStatus: z.number().int().nullable(),
  finalUrl: z.string().nullable(),
  redirectChain: z.array(z.string()),
  error: z.string().nullable(),
  productNamePresent: z.boolean().nullable(),
  penVariantPresent: z.boolean().nullable(),
  pageUpdatedOn: z.string().nullable(),
  findings: z.array(z.enum(MEDICATION_SOURCE_FINDINGS)),
});
export type MedicationSourceCheck = z.infer<typeof medicationSourceCheckSchema>;

export const medicationSourceReviewTaskSchema = z.object({
  id: z.string(),
  // TES-1361 — what the task is about: a catalogue version's links, or an
  // injection guide variant's register sources. The catalogue fields are
  // null on a guide task, and the guide fields on a catalogue task.
  subject: z.enum(["catalogue_version", "guide_variant"]),
  productId: z.string().nullable(),
  productKey: z.string().nullable(),
  productName: z.string().nullable(),
  penVariant: z.string().nullable(),
  versionId: z.string().nullable(),
  version: z.number().int().nullable(),
  // True when the version has since been replaced by a newer one.
  versionSuperseded: z.boolean(),
  instructionUrl: z.string().nullable(),
  guideVariantKey: z.string().nullable(),
  guideVariantLabel: z.string().nullable(),
  findings: z.array(z.enum(MEDICATION_SOURCE_FINDINGS)),
  openedAt: z.string(),
  lastSeenAt: z.string(),
  resolvedAt: z.string().nullable(),
  resolvedByEmployeeId: z.string().nullable(),
  resolvedSource: z.string().nullable(),
  resolutionNote: z.string().nullable(),
  // The version's latest check of each URL.
  latestChecks: z.array(medicationSourceCheckSchema),
});
export type MedicationSourceReviewTask = z.infer<
  typeof medicationSourceReviewTaskSchema
>;

// GET employee-api/v1/medication-catalogue/review-tasks[?all=1]
export const medicationSourceReviewTasksSchema = z.object({
  tasks: z.array(medicationSourceReviewTaskSchema),
});
export type MedicationSourceReviewTasks = z.infer<
  typeof medicationSourceReviewTasksSchema
>;

// POST employee-api/v1/medication-catalogue/review-tasks/:id/resolve —
// "Senast verifierad, verifierad av och källa ska auditloggas." The source
// is the page the reviewer verified against.
export const resolveMedicationSourceReviewSchema = z.object({
  source: httpsUrl,
  note: optionalText(1000).optional(),
});
export type ResolveMedicationSourceReview = z.input<
  typeof resolveMedicationSourceReviewSchema
>;

// POST employee-api/v1/medication-catalogue/source-check (admin) — run the
// weekly check now, for the caller's tenant.
export const medicationSourceCheckRunSchema = z.object({
  ran: z.boolean(),
  // "already_running" when a run was in progress.
  reason: z.string().nullable(),
  checked: z.number().int(),
  withFindings: z.number().int(),
  tasksOpened: z.number().int(),
  // TES-1361 — guide sources that could not be read this run (error state
  // recorded; since TES-1494 each also opens or extends its variant's task).
  guideSourcesFailed: z.number().int(),
});
export type MedicationSourceCheckRun = z.infer<
  typeof medicationSourceCheckRunSchema
>;

// TES-1361 (TES-1358 §6; CP-12 2026-10-14) — the injection guide's source
// register as admin reads it:
//   "För varje guidevariant ska systemets innehållsregister minst lagra läkemedel, pennvariant, officiella käll-URL:er, senast kontrollerad tidpunkt, senast observerad källversion eller innehållssignatur, Ellas innehållsversion, medicinsk granskare och publiceringsdatum."
export const injectionGuideSourceSchema = z.object({
  id: z.string(),
  kind: z.enum(INJECTION_GUIDE_SOURCE_KINDS),
  url: z.string(),
  active: z.boolean(),
  lastCheckedAt: z.string().nullable(),
  // Why the latest check could not read the source (timeout, http_503, …);
  // null when it could.
  lastCheckError: z.string().nullable(),
  lastReadAt: z.string().nullable(),
  observedVersion: z.string().nullable(),
  observedSignature: z.string().nullable(),
});
export type InjectionGuideSource = z.infer<typeof injectionGuideSourceSchema>;

export const injectionGuideVariantSchema = z.object({
  id: z.string(),
  key: z.string(),
  // Staff label, e.g. "Mounjaro KwikPen" or "Generell guide (CARE-GUIDE-01)".
  label: z.string(),
  medication: z.string().nullable(),
  penVariant: z.string().nullable(),
  articleKey: z.string().nullable(),
  // As recorded in the register.
  contentVersion: z.number().int().nullable(),
  medicalReviewer: z.string().nullable(),
  medicalReviewedAt: z.string().nullable(),
  publishedAt: z.string().nullable(),
  // The article as it stands now, so a newer published version than the
  // recorded one shows. Null when there is no article (yet).
  article: z
    .object({
      status: z.string(),
      version: z.number().int(),
    })
    .nullable(),
  sources: z.array(injectionGuideSourceSchema),
  // The variant's open review task, if any (listed with the review tasks).
  openReviewTaskId: z.string().nullable(),
});
export type InjectionGuideVariant = z.infer<typeof injectionGuideVariantSchema>;

// GET employee-api/v1/medication-catalogue/guide-register
export const injectionGuideRegisterSchema = z.object({
  variants: z.array(injectionGuideVariantSchema),
});
export type InjectionGuideRegister = z.infer<
  typeof injectionGuideRegisterSchema
>;

/** The staff label of a guide variant. */
export function injectionGuideVariantLabel(v: {
  key: string;
  medication: string | null;
  penVariant: string | null;
}): string {
  if (v.key === "general") return "Generell guide (CARE-GUIDE-01)";
  return [v.medication, v.penVariant].filter(Boolean).join(" ") || v.key;
}
